/**
 * GameDesign — the creation's MECHANICS AUTHORITY (platform-rework plan,
 * Phase A1). Derived from the creator's free-text concept by the
 * concept-analysis pass, creator-editable, and re-sent to the builder on
 * EVERY build — this doc (not a genre archetype) defines what the game IS.
 *
 * Storage mirrors the proven settings triple:
 *   - `creations.gameDesign` + `designRev` — the editable head (server-owned;
 *     written only by the updateGameDesign callable and the builder's iterate
 *     auto-amend).
 *   - `creationVersions.designSnapshot` + `designRev` (+ `designAmendment`) —
 *     the immutable per-build record. Version snapshots ARE the design
 *     history; rollback + iterate fork the design lineage exactly like the
 *     source lineage.
 *
 * The adherence gate stems `coreVerbs` + `entities[].name` and requires them
 * in the built sim's mechanics anchors — keep both lists concrete nouns/verbs
 * ("drive", "shell", "bounce"), never prose.
 */
import { PlatformError } from '@djgg/core';
import { validateJsonObject } from '@djgg/gameplay-kit';
import { ANIM_STATE_NAMES, isAnimStateName } from '../animation.js';
import { isValidAssetName, slugify } from '../slug.js';
import {
  validateGameplayDesignV1,
  validateMatchPresentationSpecV1,
  validateSceneCompositionSpecV1,
  validateEffectsBindingV1,
  type EffectsBindingV1,
  type GameplayDesignV1,
  type MatchPresentationSpecV1,
  type SceneCompositionSpecV1,
} from '../runtimeContract.js';

/**
 * Optional per-entity art plan — drives build-time sprite generation and the
 * shell's declarative render (the entity name is the sprite key). Absent for
 * legacy designs and mechanics-only entities; the generator derives a prompt
 * from name/role/theme when it's missing.
 */
export interface DesignEntitySprite {
  /** A "hero" entity gets its own generated sprite (the generator caps the sheet
   *  at a handful for legibility); non-heroes reuse a hero sprite + tint. */
  hero?: boolean;
  /** Art-direction prompt for THIS entity's sprite (subject + style cues). */
  prompt?: string;
  /** 'neutral' ⇒ generate a grayscale sprite for clean runtime recolor
   *  (teams/players); 'fixed' ⇒ keep the sprite's own colors. */
  tint?: 'neutral' | 'fixed';
  /** The one short sign this sprite's art carries, verbatim ("POW"): the
   *  generator prints exactly it and the judge allows exactly it. Absent ⇒ the
   *  art carries no text at all. Uppercase letters, digits, spaces and !?-. */
  label?: string;
  /** Machine-readable contract for how the generated pixels are oriented.
   * Optional only for designs authored before this contract existed. New art
   * plans declare it so generation, judging, and game code never have to infer
   * facing, mirroring, rotation, or effect origins from prose or filenames. */
  orientation?: SpriteOrientationContract;
  /** Present exactly when this sprite is a TERRAIN TILE: level surface the
   * game repeats along a collision box (a floor, girder, catwalk, platform,
   * ladder), never a free-standing object. Generation draws it as one flat
   * band of its material at this proportion, and the judge grades it as
   * terrain (a grated catwalk read as a gate or window is the failure this
   * closes — bounty-bob-strikes-back-composed-6, 2026-10-20). */
  surfaceTile?: SpriteSurfaceTile;
  /** Explicit animation-state declarations for this entity (see
   *  {@link DesignEntityAnimation}). Absent ⇒ the plan composer derives the
   *  tier policy itself; present ⇒ honored under the per-build strip cap. */
  animations?: DesignEntityAnimation[];
}

/** A terrain tile's frame: the width ÷ height of the box the game tiles it
 * over (its render size). */
export interface SpriteSurfaceTile {
  aspect: number;
}

export const SPRITE_DIRECTIONS = [
  'none', 'left', 'right', 'up', 'down',
  'up-left', 'up-right', 'down-left', 'down-right',
] as const;
export type SpriteDirection = (typeof SPRITE_DIRECTIONS)[number];

export const SPRITE_PROJECTIONS = [
  'side', 'top-down', 'isometric', 'three-quarter', 'front', 'symmetric',
] as const;
export type SpriteProjection = (typeof SPRITE_PROJECTIONS)[number];

export interface SpriteNormalizedPoint {
  /** Image-space position: 0=left, 1=right. */
  x: number;
  /** Image-space position: 0=top, 1=bottom. */
  y: number;
}

export interface SpriteForwardVector {
  /** Image-space horizontal direction: -1, 0, or 1. */
  x: -1 | 0 | 1;
  /** Image-space vertical direction: -1, 0, or 1. */
  y: -1 | 0 | 1;
}

export interface SpriteAttachmentPoint extends SpriteNormalizedPoint {
  /** Stable semantic id such as `muzzle`, `bomb-bay`, `feet`, or `center`. */
  id: string;
}

/** Exact orientation/alignment contract for one generated sprite. Coordinates
 * are normalized image-space values and survive alongside the art provenance. */
export interface SpriteOrientationContract {
  projection: SpriteProjection;
  canonicalFacing: SpriteDirection;
  forwardVector: SpriteForwardVector;
  pivot: SpriteNormalizedPoint;
  movementAxis: 'horizontal' | 'vertical' | 'omnidirectional' | 'stationary';
  mirrorSafe: boolean;
  rotationPolicy: 'none' | 'quarter-turns' | 'free';
  /** Directions supplied as distinct images/frames rather than transforms. */
  directionalVariants?: SpriteDirection[];
  /** Where gameplay effects originate on the untransformed canonical image. */
  attachments?: SpriteAttachmentPoint[];
}

/** One declared animation state on an entity's art plan. `state` is a member
 *  of the closed ANIM_STATE_NAMES vocabulary; `label`/`prompt` are optional
 *  creator hints for what the motion depicts (fed to strip generation). */
export interface DesignEntityAnimation {
  state: string;
  /** Short display label ("wing flap"). */
  label?: string;
  /** Extra art direction for this state's strip. */
  prompt?: string;
}

/** One entity the game's sim must realize. */
export interface DesignEntity {
  /** Concrete noun ("tank", "shell", "obstacle") — adherence-gate checked. */
  name: string;
  /** What it does / why it exists, one sentence. */
  role: string;
  /** Optional art plan (see {@link DesignEntitySprite}). */
  sprite?: DesignEntitySprite;
}

/**
 * One selectable chapter — a level, stage, boss or episode. Declaring these is
 * how a design says "this game has a level select": the builder stamps them
 * into creation.json and the SHELL renders the picker in platform chrome,
 * handing the chosen {@link DesignChapter.id} to `createSim` as `chapter`.
 *
 * A sim must NEVER build its own selection screen — pre-match navigation is
 * not simulation, and a hand-rolled one renders outside the chrome and misses
 * the platform's input plumbing.
 */
export interface DesignChapter {
  /** Stable slug handed to the sim as `chapter` — never renumbered (cleared
   *  progress is keyed by it). */
  id: string;
  /** Tile label ("Central Highway", "Chill Penguin"). */
  label: string;
  /** Optional one-line description shown under the label. */
  blurb?: string;
  /** Optional tile art: an entity name (its generated sprite) or a scenery
   *  background name. Absent ⇒ the tile draws a palette placeholder. */
  sprite?: string;
  /** Optional gate: the chapter id that must be CLEARED before this unlocks.
   *  Absent ⇒ open from the start. */
  unlockedBy?: string;
  /** Optional generic linear successor. Rich level mechanics stay in the
   *  discriminated platformer facet; the shell consumes only this stable id. */
  nextChapterId?: string;
  /** Whether clearing this chapter should immediately launch its successor.
   *  Absent preserves the legacy selector behavior. */
  autoAdvance?: boolean;
}

/**
 * Player-facing onboarding copy — the ONLY design content the shell's How To
 * Play deck reads for its prose pages (creation-shell 0.29.0). One idea per
 * page, so every string here is a tight, spoken-aloud line rather than a
 * paragraph: `hook` sells the game on page 1, `goal` says how you win on page
 * 3, `tips` are up to three "here's the trick" lines on page 4.
 *
 * Deliberately SEPARATE from `summary`/`winCondition`, which are written for
 * the BUILD MODEL (mechanics authority) and read like a spec. Absent is legal
 * — the deck falls back to those fields page by page, so the whole existing
 * corpus renders the new deck on its next build without this block.
 *
 * There is NO controls field here on purpose: the platform DRAWS the controls
 * page from the resolved controls plan (real keycaps / the active touch
 * scheme's gestures), so authored controls prose could only contradict the
 * device in the player's hand.
 */
export interface DesignOnboarding {
  /** Page 1: what this game is, in the voice you'd use to talk a friend into
   *  playing it. One or two short sentences. */
  hook: string;
  /** Page 3: how you win, said the way a player would say it. */
  goal: string;
  /** Page 4: up to three short "how to actually play well" lines. Absent (or
   *  empty) omits the tips page entirely. */
  tips?: string[];
}

/** The design doc. All prose fields are creator-visible and creator-editable. */
export interface GameDesign {
  /** 1–2 sentences: what the game IS. */
  summary: string;
  /** The playable verbs ("drive", "rotate turret", "fire shell", "bounce
   *  shots") — the adherence gate's checklist. Concrete, lowercase. */
  coreVerbs: string[];
  /** Binding rule bullets — the mechanics contract the build must realize. */
  mechanics: string[];
  /** The entities the sim must model. */
  entities: DesignEntity[];
  /** What the inputs DO (hardware coverage stays in settings.inputScheme). */
  controls: string;
  /** Win/lose/scoring shape. */
  winCondition: string;
  /** Optional per-mode mechanics notes (the mode SET itself lives in settings). */
  modeNotes?: string;
  /** Optional visual direction — guidance, never binding. */
  theme?: string;
  /** Optional shared art direction — steers the generated sprite sheet +
   *  backgrounds and owns the working palette (UI/HUD/tint/fallbacks). */
  artDirection?: ArtDirection;
  /** Optional non-entity scenery to generate + bind (backgrounds today;
   *  tileable textures are a follow-up). */
  scenery?: DesignScenery;
  /** Optional selectable chapters (levels / stages / bosses), in menu order.
   *  Two or more give the creation a shell-rendered level select; the sim is
   *  handed the chosen id and starts in it. See {@link DesignChapter}. */
  chapters?: DesignChapter[];
  /** Optional player-facing onboarding copy for the shell's How To Play deck.
   *  See {@link DesignOnboarding} — absent falls back to summary/winCondition. */
  onboarding?: DesignOnboarding;
  /** Optional reviewed gameplay authority. Existing prose remains visible
   * context, while this strict facet is what a future runtime contract may
   * snapshot. The prompt renderer intentionally does not change in Phase 1. */
  gameplay?: GameplayDesignV1;
  /** Static composition declarations for a future shared HUD/scene layer. */
  presentation?: {
    contractVersion: 1;
    hud: MatchPresentationSpecV1;
    scene: SceneCompositionSpecV1;
    effects: EffectsBindingV1;
  };
  /** Creator-visible audio direction only. Runtime media remains platform
   * stamped and is never inferred from these optional briefs. */
  audio?: {
    contractVersion: 1;
    musicBrief?: string;
    cueStyle?: string;
  };
  /** LEGACY passthrough for the RETIRED platformer family (2026-09-03).
   *  The 7 frozen platformer-era creations carry this facet in stored
   *  designs and version snapshots; it is never validated, rendered, or
   *  written by new code. The full contract lives on the
   *  `archive/platformer-rooms` branch. */
  platformer?: unknown;
}

/** Additive builder provenance for one stable creator mechanic. */
export interface PlatformerMechanicOutcome {
  mechanicId: string;
  status: 'implemented' | 'constraint';
  reason?: string;
}

/** Creator-visible art problem for one primary actor. */
export interface PlatformerPrimaryActorAssetIssue {
  entityId: string;
  entityName: string;
  assetName: string;
  reason: 'missing' | 'unusable';
}

/** Shared art direction for a creation's generated art. */
export interface ArtDirection {
  /** Medium/style ("flat cel-shaded", "16-bit pixel art", "hand-painted"…). */
  style: string;
  /** Optional mood/setting cue. */
  mood?: string;
  /** Optional small ordered working palette (#rrggbb) for UI/HUD/tint/fallbacks
   *  — supersedes the settings palette preset when present. */
  palette?: string[];
}

/** Non-entity scenery to generate for a creation. */
export interface DesignScenery {
  /** Full-scene backgrounds to generate + bind to `background.sprite`. */
  backgrounds: SceneryBackground[];
  /** Whether a body in this game stands on, walks along, climbs or lands on
   * surfaces the game draws itself — declared by a design whose bodies are
   * known (a composition's in-memory art plan, `compositionMediaDesign`).
   * Never authored or parsed: {@link validateGameDesign} drops it, so a
   * free-form design never declares it and its background keeps every
   * playable structure out (`designSceneryRuleV1`, `spec/sceneryOnly.ts`). */
  surfaceContact?: boolean;
}

/** One generated background scene. */
export interface SceneryBackground {
  /** Stable asset name (a valid slug) — the manifest binds it as the background. */
  name: string;
  /** Art-direction prompt for the background scene. */
  prompt: string;
}

/** One fetched-and-distilled reference the creator supplied at concept time
 *  (`creations.referenceBriefs`). Summaries are LLM-extracted ONCE at analysis
 *  and re-sent to every build — raw pages are never stored or refetched. */
export interface ReferenceBrief {
  /** The creator-supplied https URL (provenance). */
  url: string;
  /** Page title (or the URL when extraction found none). */
  title: string;
  /** Extractive summary of the game-relevant content (~2KB cap). '' when
   *  status is 'failed'. */
  summary: string;
  /** 'failed' = the fetch/summarize failed; the build proceeds concept-only
   *  and the wizard shows the marker. Never silently dropped. */
  status: 'ok' | 'failed';
  /** Millis of the (single) fetch. */
  fetchedAt: number;
}

/**
 * What a vision model saw while WATCHING a reference game — the observational
 * half of a {@link VideoBrief}. Every field is what the frames showed, never
 * what the model knows about the title from training: the whole point of
 * watching is to ground the design in pixels the creator can point at.
 */
export interface VideoObservations {
  /** 1-2 sentences: what the game IS, read off the footage. */
  summary: string;
  /** The art direction in medium/style terms ("chunky 16-bit pixel art,
   *  high-contrast rim lighting") — feeds artDirection.style downstream. */
  artStyle: string;
  /** Working palette sampled from the frames (≤8 '#rrggbb'). */
  palette: string[];
  /** How the world is framed: 'single-screen' | 'side-scrolling' |
   *  'vertical-scrolling' | 'top-down-scrolling' | 'grid-board'. The one
   *  observation that decides engineHint, so it is a closed vocabulary. */
  worldStructure: VideoWorldStructure;
  /** On-screen actors identified, lowercase 1-3 words ("lander", "gun turret"). */
  entities: string[];
  /** Mechanics visible IN MOTION — what things do, not what a wiki says. */
  features: string[];
  /** HUD/readout elements observed ("score, top-left", "fuel bar"). */
  hud: string[];
}

/** The closed world-structure vocabulary a watch can report. Mirrors the
 *  distinction `checkWorldStructure` enforces between single-screen and
 *  scrolling engine hints — same words, one dialect. */
export const VIDEO_WORLD_STRUCTURES = [
  'single-screen',
  'side-scrolling',
  'vertical-scrolling',
  'top-down-scrolling',
  'grid-board',
] as const;
export type VideoWorldStructure = (typeof VIDEO_WORLD_STRUCTURES)[number];

/**
 * One reference GAME watched on video (`creations.videoBriefs`).
 *
 * Where {@link ReferenceBrief} distills a page the creator PASTED, this is the
 * enrichment pass for a game the creator merely NAMED ("like Choplifter, but
 * underwater"): the platform finds gameplay footage, samples frames across the
 * run, and has a vision model read the look and the loop off them. Written
 * ONCE at analysis (or at an iteration that introduces a new reference) and
 * re-sent to every build — frames are never stored, only what was seen.
 */
export interface VideoBrief {
  /** The referenced game as detected in the creator's text ("Choplifter"). */
  game: string;
  /** YouTube video id — the provenance the creator can go watch themselves. */
  videoId: string;
  /** Published video title (provenance + the creator-facing label). */
  title: string;
  /** Publishing channel (provenance). */
  channel: string;
  /** How many frames the vision model was actually shown. 0 when failed. */
  frameCount: number;
  /** The read. Absent when status is 'failed'. */
  observations?: VideoObservations;
  /** 'failed' = search, capture, or the vision read failed. The build proceeds
   *  WITHOUT this brief — enrichment is advisory and never blocks. The marker
   *  is kept so the wizard can say the watch was attempted and missed. */
  status: 'ok' | 'failed';
  /** Millis of the (single) watch. */
  watchedAt: number;
}

/** Field caps — one source for the validator, the wizard editor, and the
 *  concept-analysis output check. */
export const DESIGN_LIMITS = {
  summaryMax: 500,
  coreVerbsMax: 12,
  coreVerbMaxLen: 40,
  mechanicsMax: 20,
  mechanicMaxLen: 300,
  entitiesMax: 20,
  entityNameMaxLen: 60,
  entityRoleMaxLen: 200,
  controlsMax: 500,
  winConditionMax: 300,
  modeNotesMax: 500,
  themeMax: 300,
  // Art plan (build-time asset generation).
  spritePromptMax: 300,
  spriteLabelMaxLen: 12,
  /** A terrain tile's width/height (or height/width) ceiling. */
  surfaceTileAspectMax: 100,
  animationsMaxPerEntity: 8,
  animationLabelMaxLen: 40,
  artStyleMax: 120,
  artMoodMax: 200,
  paletteMax: 8,
  sceneryMax: 2,
  sceneryNameMaxLen: 40,
  sceneryPromptMax: 300,
  // Chapters (shell-rendered level select). The cap is the grid's readable
  // ceiling, not an arbitrary number: menuLayout tops out at 4 columns.
  chaptersMax: 16,
  chapterIdMaxLen: 40,
  chapterLabelMaxLen: 60,
  chapterBlurbMax: 120,
  // Onboarding (the shell's How To Play deck). These caps are LAYOUT limits,
  // not style advice: each page renders ONE idea at display size on a 480-px-
  // wide phone canvas, so a hook/goal past ~200 chars and a tip past ~120 can
  // only shrink through `fitContent` until the page stops reading as a
  // sticker card. Sized against the neighbours that share a page's worth of
  // room — winConditionMax 300 for a spec line, chapterBlurbMax 120 for a
  // one-line tile caption.
  onboardingHookMax: 200,
  onboardingGoalMax: 200,
  onboardingTipsMax: 3,
  onboardingTipMaxLen: 120,
} as const;

/** A sprite label: short, uppercase, prompt-safe (no quotes or punctuation
 *  that could close the generator's quoted label). */
const SPRITE_LABEL_RE = new RegExp(`^[A-Z0-9!?-](?:[A-Z0-9 !?-]{0,${DESIGN_LIMITS.spriteLabelMaxLen - 2}}[A-Z0-9!?-])?$`);
export function isSpriteLabel(value: unknown): value is string {
  return typeof value === 'string' && SPRITE_LABEL_RE.test(value);
}

/** Max reference URLs a creation may carry (fetch + summarize cost bound). */
export const MAX_REFERENCE_BRIEFS = 3;
export const REFERENCE_SUMMARY_MAX = 2500;

function fail(msg: string): never {
  throw new PlatformError('validation', `invalid GameDesign: ${msg}`);
}

function reqText(v: unknown, what: string, max: number): string {
  if (typeof v !== 'string') fail(`${what} must be a string`);
  const s = (v as string).trim();
  if (!s) fail(`${what} is required`);
  if (s.length > max) fail(`${what} exceeds ${max} chars (got ${s.length})`);
  return s;
}

function optText(v: unknown, what: string, max: number): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'string') fail(`${what} must be a string`);
  const s = (v as string).trim();
  if (s.length > max) fail(`${what} exceeds ${max} chars (got ${s.length})`);
  return s || undefined;
}

function stableIdentifier(v: unknown, what: string): string {
  const id = reqText(v, what, DESIGN_LIMITS.chapterIdMaxLen);
  if (!isValidAssetName(id)) fail(`${what} must be a valid identifier`);
  return id;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Like {@link optText} but single-line: control chars + newlines are stripped
 *  (collapsed to one space) BEFORE the length check — animation labels/prompts
 *  interpolate into generation prompts, where an embedded newline is an
 *  injection surface, not content. */
function optLineText(v: unknown, what: string, max: number): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'string') fail(`${what} must be a string`);
  // eslint-disable-next-line no-control-regex
  const s = (v as string).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/ {2,}/g, ' ').trim();
  if (s.length > max) fail(`${what} exceeds ${max} chars (got ${s.length})`);
  return s || undefined;
}

/** Validate an entity's declared animation states: closed-vocabulary members,
 *  no duplicate states, capped count, sanitized single-line label/prompt.
 *  Returns undefined when the list is absent or empty. */
function validateEntityAnimations(v: unknown, what: string): DesignEntityAnimation[] | undefined {
  if (v === undefined || v === null) return undefined;
  if (!Array.isArray(v)) fail(`${what} must be an array`);
  if (v.length > DESIGN_LIMITS.animationsMaxPerEntity) {
    fail(`${what} exceeds ${DESIGN_LIMITS.animationsMaxPerEntity} entries`);
  }
  const seen = new Set<string>();
  const out = v.map((a, i) => {
    if (typeof a !== 'object' || a === null || Array.isArray(a)) {
      fail(`${what}[${i}] must be an object`);
    }
    const aa = a as Record<string, unknown>;
    if (typeof aa.state !== 'string' || !isAnimStateName(aa.state)) {
      fail(
        `${what}[${i}].state must be an animation state (closed vocabulary: ${ANIM_STATE_NAMES.join(', ')})`,
      );
    }
    const state = aa.state as string;
    if (seen.has(state)) fail(`${what} declares state "${state}" more than once`);
    seen.add(state);
    const label = optLineText(aa.label, `${what}[${i}].label`, DESIGN_LIMITS.animationLabelMaxLen);
    const prompt = optLineText(aa.prompt, `${what}[${i}].prompt`, DESIGN_LIMITS.spritePromptMax);
    return {
      state,
      ...(label !== undefined ? { label } : {}),
      ...(prompt !== undefined ? { prompt } : {}),
    };
  });
  return out.length > 0 ? out : undefined;
}

function optBool(v: unknown, what: string): boolean | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'boolean') fail(`${what} must be a boolean`);
  return v;
}

const DIRECTION_VECTOR: Record<SpriteDirection, SpriteForwardVector> = {
  none: { x: 0, y: 0 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 },
  up: { x: 0, y: -1 }, down: { x: 0, y: 1 },
  'up-left': { x: -1, y: -1 }, 'up-right': { x: 1, y: -1 },
  'down-left': { x: -1, y: 1 }, 'down-right': { x: 1, y: 1 },
};

function normalizedPoint(v: unknown, what: string): SpriteNormalizedPoint {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) fail(`${what} must be an object`);
  const p = v as Record<string, unknown>;
  if (![p.x, p.y].every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1)) {
    fail(`${what}.x/y must be finite numbers from 0 to 1`);
  }
  return { x: p.x as number, y: p.y as number };
}

function validateSpriteOrientation(v: unknown, what: string): SpriteOrientationContract | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'object' || Array.isArray(v)) fail(`${what} must be an object`);
  const o = v as Record<string, unknown>;
  if (typeof o.projection !== 'string' || !SPRITE_PROJECTIONS.includes(o.projection as SpriteProjection)) {
    fail(`${what}.projection must be one of ${SPRITE_PROJECTIONS.join(', ')}`);
  }
  if (typeof o.canonicalFacing !== 'string' || !SPRITE_DIRECTIONS.includes(o.canonicalFacing as SpriteDirection)) {
    fail(`${what}.canonicalFacing must be one of ${SPRITE_DIRECTIONS.join(', ')}`);
  }
  if (typeof o.forwardVector !== 'object' || o.forwardVector === null || Array.isArray(o.forwardVector)) {
    fail(`${what}.forwardVector must be an object`);
  }
  const rawVector = o.forwardVector as Record<string, unknown>;
  if (![-1, 0, 1].includes(rawVector.x as number) || ![-1, 0, 1].includes(rawVector.y as number)) {
    fail(`${what}.forwardVector.x/y must be -1, 0, or 1`);
  }
  const canonicalFacing = o.canonicalFacing as SpriteDirection;
  const expected = DIRECTION_VECTOR[canonicalFacing];
  if (rawVector.x !== expected.x || rawVector.y !== expected.y) {
    fail(`${what}.forwardVector must match canonicalFacing ${canonicalFacing}`);
  }
  const movementAxes = ['horizontal', 'vertical', 'omnidirectional', 'stationary'] as const;
  if (typeof o.movementAxis !== 'string' || !movementAxes.includes(o.movementAxis as typeof movementAxes[number])) {
    fail(`${what}.movementAxis must be one of ${movementAxes.join(', ')}`);
  }
  if (typeof o.mirrorSafe !== 'boolean') fail(`${what}.mirrorSafe must be a boolean`);
  const rotations = ['none', 'quarter-turns', 'free'] as const;
  if (typeof o.rotationPolicy !== 'string' || !rotations.includes(o.rotationPolicy as typeof rotations[number])) {
    fail(`${what}.rotationPolicy must be one of ${rotations.join(', ')}`);
  }
  let directionalVariants: SpriteDirection[] | undefined;
  if (o.directionalVariants !== undefined && o.directionalVariants !== null) {
    if (!Array.isArray(o.directionalVariants) || o.directionalVariants.length > 8) {
      fail(`${what}.directionalVariants must be an array of at most 8 directions`);
    }
    directionalVariants = [...new Set(o.directionalVariants.map((d, i) => {
      if (typeof d !== 'string' || !SPRITE_DIRECTIONS.includes(d as SpriteDirection) || d === 'none') {
        fail(`${what}.directionalVariants[${i}] must be a concrete sprite direction`);
      }
      return d as SpriteDirection;
    }))];
    if (directionalVariants.length === 0) directionalVariants = undefined;
  }
  let attachments: SpriteAttachmentPoint[] | undefined;
  if (o.attachments !== undefined && o.attachments !== null) {
    if (!Array.isArray(o.attachments) || o.attachments.length > 8) {
      fail(`${what}.attachments must be an array of at most 8 points`);
    }
    const seen = new Set<string>();
    attachments = o.attachments.map((entry, i) => {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
        fail(`${what}.attachments[${i}] must be an object`);
      }
      const a = entry as Record<string, unknown>;
      const id = stableIdentifier(a.id, `${what}.attachments[${i}].id`);
      if (seen.has(id)) fail(`${what}.attachments contains duplicate id ${id}`);
      seen.add(id);
      return { id, ...normalizedPoint(a, `${what}.attachments[${i}]`) };
    });
    if (attachments.length === 0) attachments = undefined;
  }
  return {
    projection: o.projection as SpriteProjection,
    canonicalFacing,
    forwardVector: { x: rawVector.x as -1 | 0 | 1, y: rawVector.y as -1 | 0 | 1 },
    pivot: normalizedPoint(o.pivot, `${what}.pivot`),
    movementAxis: o.movementAxis as SpriteOrientationContract['movementAxis'],
    mirrorSafe: o.mirrorSafe,
    rotationPolicy: o.rotationPolicy as SpriteOrientationContract['rotationPolicy'],
    ...(directionalVariants ? { directionalVariants } : {}),
    ...(attachments ? { attachments } : {}),
  };
}

/** Validate an optional per-entity art plan; returns undefined when empty so an
 *  entity with no meaningful sprite hints stays `{name, role}`. */
function validateEntitySprite(v: unknown, what: string): DesignEntitySprite | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'object' || Array.isArray(v)) fail(`${what} must be an object`);
  const s = v as Record<string, unknown>;
  const hero = optBool(s.hero, `${what}.hero`);
  const prompt = optText(s.prompt, `${what}.prompt`, DESIGN_LIMITS.spritePromptMax);
  let tint: 'neutral' | 'fixed' | undefined;
  if (s.tint !== undefined && s.tint !== null) {
    if (s.tint !== 'neutral' && s.tint !== 'fixed') fail(`${what}.tint must be 'neutral' or 'fixed'`);
    tint = s.tint;
  }
  let label: string | undefined;
  if (s.label !== undefined && s.label !== null) {
    if (!isSpriteLabel(s.label)) {
      fail(`${what}.label must be 1..${DESIGN_LIMITS.spriteLabelMaxLen} uppercase letters, digits, spaces or !?-`);
    }
    label = s.label;
  }
  const animations = validateEntityAnimations(s.animations, `${what}.animations`);
  const orientation = validateSpriteOrientation(s.orientation, `${what}.orientation`);
  let surfaceTile: SpriteSurfaceTile | undefined;
  if (s.surfaceTile !== undefined && s.surfaceTile !== null) {
    const t = s.surfaceTile as Record<string, unknown>;
    if (typeof t !== 'object' || Array.isArray(t) || Object.keys(t).some(key => key !== 'aspect')
      || typeof t.aspect !== 'number' || !Number.isFinite(t.aspect)
      || t.aspect < 1 / DESIGN_LIMITS.surfaceTileAspectMax || t.aspect > DESIGN_LIMITS.surfaceTileAspectMax) {
      fail(`${what}.surfaceTile must be { aspect } with aspect a width/height ratio in [1/${DESIGN_LIMITS.surfaceTileAspectMax}, ${DESIGN_LIMITS.surfaceTileAspectMax}]`);
    }
    surfaceTile = { aspect: t.aspect };
  }
  const out: DesignEntitySprite = {
    ...(hero !== undefined ? { hero } : {}),
    ...(prompt !== undefined ? { prompt } : {}),
    ...(tint !== undefined ? { tint } : {}),
    ...(label !== undefined ? { label } : {}),
    ...(orientation !== undefined ? { orientation } : {}),
    ...(surfaceTile !== undefined ? { surfaceTile } : {}),
    ...(animations !== undefined ? { animations } : {}),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

function validatePalette(v: unknown, what: string): string[] | undefined {
  if (v === undefined || v === null) return undefined;
  if (!Array.isArray(v)) fail(`${what} must be an array`);
  if (v.length > DESIGN_LIMITS.paletteMax) fail(`${what} exceeds ${DESIGN_LIMITS.paletteMax} colors`);
  const colors = v.map((c, i) => {
    if (typeof c !== 'string' || !HEX_COLOR.test(c)) fail(`${what}[${i}] must be a #rrggbb color`);
    return c.toLowerCase();
  });
  return colors.length > 0 ? colors : undefined;
}

function validateArtDirection(v: unknown): ArtDirection | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'object' || Array.isArray(v)) fail('artDirection must be an object');
  const a = v as Record<string, unknown>;
  // Clamp the style hint to its ceiling rather than rejecting the whole design
  // over an overlong blurb — it's presentation copy, not a structural field,
  // and an extra sentence must never sink concept analysis.
  if (typeof a.style !== 'string' || !a.style.trim()) fail('artDirection.style is required');
  const style = a.style.trim().slice(0, DESIGN_LIMITS.artStyleMax);
  const mood = optText(a.mood, 'artDirection.mood', DESIGN_LIMITS.artMoodMax);
  const palette = validatePalette(a.palette, 'artDirection.palette');
  return {
    style,
    ...(mood !== undefined ? { mood } : {}),
    ...(palette !== undefined ? { palette } : {}),
  };
}

function validateScenery(v: unknown): DesignScenery | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'object' || Array.isArray(v)) fail('scenery must be an object');
  const s = v as Record<string, unknown>;
  if (!Array.isArray(s.backgrounds)) fail('scenery.backgrounds must be an array');
  const seen = new Set<string>();
  const backgrounds: SceneryBackground[] = [];
  // Normalize/drop each entry rather than rejecting the whole design, and cap
  // the RESULT at the scenery ceiling (so junk entries don't consume the
  // budget ahead of good ones). Scenery is optional flavor: an extra
  // background, an un-sluggable name, or a blank prompt trims that one
  // background — none is worth killing concept analysis (the platform's most
  // important path). The name is slugified here for the same reason entity
  // names are tolerated raw and slugified downstream: LLMs emit "Arena
  // Background", not "arena-background".
  for (const b of s.backgrounds) {
    if (backgrounds.length >= DESIGN_LIMITS.sceneryMax) break;
    if (typeof b !== 'object' || b === null) continue;
    const bb = b as Record<string, unknown>;
    if (typeof bb.name !== 'string' || typeof bb.prompt !== 'string') continue;
    const prompt = bb.prompt.trim().slice(0, DESIGN_LIMITS.sceneryPromptMax);
    if (!prompt) continue;
    const name = slugify(bb.name).slice(0, DESIGN_LIMITS.sceneryNameMaxLen).replace(/-+$/g, '');
    if (!isValidAssetName(name) || seen.has(name)) continue;
    seen.add(name);
    backgrounds.push({ name, prompt });
  }
  // An empty backgrounds list carries no scenery — drop it.
  return backgrounds.length > 0 ? { backgrounds } : undefined;
}

/**
 * Validate the optional chapter list. Beyond the shape, two rules exist because
 * this list is LLM-generated and a bad one reaches players as a broken menu:
 *
 *  - ids must be UNIQUE — duplicates would make "cleared" ambiguous and let two
 *    tiles launch the same stage while claiming to be different ones;
 *  - a lone chapter is DROPPED — a one-tile picker is not a choice, and the
 *    shell would skip it anyway (routeChapters). Dropping it here keeps one
 *    representation instead of two that render identically.
 *
 * An `unlockedBy` pointing at a missing id is deliberately NOT fatal: the shell
 * fails it open (chapterModel), so a typo costs nothing and must not sink an
 * otherwise good build.
 */
function validateChapters(v: unknown, retainSingle = false): DesignChapter[] | undefined {
  if (v === undefined || v === null) return undefined;
  if (!Array.isArray(v)) fail('chapters must be an array');
  if (v.length > DESIGN_LIMITS.chaptersMax) {
    fail(`chapters exceeds ${DESIGN_LIMITS.chaptersMax} entries`);
  }
  const seen = new Set<string>();
  const chapters = v.map((c, i) => {
    if (typeof c !== 'object' || c === null) fail(`chapters[${i}] must be an object`);
    const cc = c as Record<string, unknown>;
    const id = reqText(cc.id, `chapters[${i}].id`, DESIGN_LIMITS.chapterIdMaxLen);
    if (!isValidAssetName(id)) {
      fail(`chapters[${i}].id must be a valid slug (2..40, lowercase)`);
    }
    if (seen.has(id)) fail(`chapters[${i}].id "${id}" is duplicated`);
    seen.add(id);
    const label = reqText(cc.label, `chapters[${i}].label`, DESIGN_LIMITS.chapterLabelMaxLen);
    const blurb = optText(cc.blurb, `chapters[${i}].blurb`, DESIGN_LIMITS.chapterBlurbMax);
    const sprite = optText(cc.sprite, `chapters[${i}].sprite`, DESIGN_LIMITS.chapterIdMaxLen);
    const unlockedBy = optText(
      cc.unlockedBy,
      `chapters[${i}].unlockedBy`,
      DESIGN_LIMITS.chapterIdMaxLen,
    );
    const nextChapterId = optText(
      cc.nextChapterId,
      `chapters[${i}].nextChapterId`,
      DESIGN_LIMITS.chapterIdMaxLen,
    );
    if (nextChapterId !== undefined && !isValidAssetName(nextChapterId)) {
      fail(`chapters[${i}].nextChapterId must be a valid slug (2..40, lowercase)`);
    }
    if (cc.autoAdvance !== undefined && typeof cc.autoAdvance !== 'boolean') {
      fail(`chapters[${i}].autoAdvance must be a boolean`);
    }
    return {
      id,
      label,
      ...(blurb !== undefined ? { blurb } : {}),
      ...(sprite !== undefined ? { sprite } : {}),
      ...(unlockedBy !== undefined ? { unlockedBy } : {}),
      ...(nextChapterId !== undefined ? { nextChapterId } : {}),
      ...(cc.autoAdvance !== undefined ? { autoAdvance: cc.autoAdvance as boolean } : {}),
    };
  });
  return chapters.length > 1 || (retainSingle && chapters.length === 1) ? chapters : undefined;
}

/**
 * Validate the optional onboarding block.
 *
 * Same posture as {@link validateArtDirection}, for the same reason: this is
 * PRESENTATION copy with a total fallback (the deck reads summary/winCondition
 * when it is absent), so an over-long line CLAMPS rather than sinking concept
 * analysis — the platform's most important path — over a sentence the shell
 * would have shrunk anyway. What DOES fail is a structurally wrong block: a
 * half-written `{hook}` with no goal is the model losing the shape, and the
 * proposal pass's retry-once contract is the right place to catch that.
 *
 * `tips` follows {@link validateScenery}: junk entries are dropped, the list
 * caps at {@link DESIGN_LIMITS.onboardingTipsMax}, and an empty result drops
 * the field (the deck then omits the tips page).
 */
function validateOnboarding(v: unknown): DesignOnboarding | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'object' || Array.isArray(v)) fail('onboarding must be an object');
  const o = v as Record<string, unknown>;
  const L = DESIGN_LIMITS;
  if (typeof o.hook !== 'string' || !o.hook.trim()) fail('onboarding.hook is required');
  if (typeof o.goal !== 'string' || !o.goal.trim()) fail('onboarding.goal is required');
  const hook = o.hook.trim().slice(0, L.onboardingHookMax);
  const goal = o.goal.trim().slice(0, L.onboardingGoalMax);
  const tips: string[] = [];
  if (o.tips !== undefined && o.tips !== null) {
    if (!Array.isArray(o.tips)) fail('onboarding.tips must be an array');
    for (const t of o.tips) {
      if (tips.length >= L.onboardingTipsMax) break;
      if (typeof t !== 'string') continue;
      const tip = t.trim().slice(0, L.onboardingTipMaxLen);
      if (tip) tips.push(tip);
    }
  }
  return { hook, goal, ...(tips.length > 0 ? { tips } : {}) };
}

/**
 * Validate an untrusted value as a {@link GameDesign}; returns a NORMALIZED
 * copy (trimmed strings, empty optionals dropped). Throws
 * `PlatformError('validation')` naming the first problem. An empty
 * `coreVerbs` is rejected — a design the adherence gate can't check is not a
 * design (the concept-analysis retry contract depends on this).
 */
export function validateGameDesign(value: unknown): GameDesign {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail('not an object');
  }
  const v = value as Record<string, unknown>;
  const L = DESIGN_LIMITS;

  const summary = reqText(v.summary, 'summary', L.summaryMax);

  if (!Array.isArray(v.coreVerbs) || v.coreVerbs.length === 0) {
    fail('coreVerbs must be a non-empty array');
  }
  if (v.coreVerbs.length > L.coreVerbsMax) fail(`coreVerbs exceeds ${L.coreVerbsMax} entries`);
  const coreVerbs = v.coreVerbs.map((x, i) =>
    reqText(x, `coreVerbs[${i}]`, L.coreVerbMaxLen).toLowerCase(),
  );

  if (!Array.isArray(v.mechanics) || v.mechanics.length === 0) {
    fail('mechanics must be a non-empty array');
  }
  if (v.mechanics.length > L.mechanicsMax) fail(`mechanics exceeds ${L.mechanicsMax} entries`);
  const mechanics = v.mechanics.map((x, i) => reqText(x, `mechanics[${i}]`, L.mechanicMaxLen));

  if (!Array.isArray(v.entities) || v.entities.length === 0) {
    fail('entities must be a non-empty array');
  }
  if (v.entities.length > L.entitiesMax) fail(`entities exceeds ${L.entitiesMax} entries`);
  const entities = v.entities.map((e, i) => {
    if (typeof e !== 'object' || e === null) fail(`entities[${i}] must be an object`);
    const ee = e as Record<string, unknown>;
    const sprite = validateEntitySprite(ee.sprite, `entities[${i}].sprite`);
    return {
      name: reqText(ee.name, `entities[${i}].name`, L.entityNameMaxLen).toLowerCase(),
      role: reqText(ee.role, `entities[${i}].role`, L.entityRoleMaxLen),
      ...(sprite !== undefined ? { sprite } : {}),
    };
  });

  const controls = reqText(v.controls, 'controls', L.controlsMax);
  const winCondition = reqText(v.winCondition, 'winCondition', L.winConditionMax);
  const modeNotes = optText(v.modeNotes, 'modeNotes', L.modeNotesMax);
  const theme = optText(v.theme, 'theme', L.themeMax);
  const artDirection = validateArtDirection(v.artDirection);
  const scenery = validateScenery(v.scenery);
  // LEGACY: a frozen platformer-era design keeps its single chapter (the
  // retired facet's v2 contract allowed one) and its opaque facet — the
  // passthrough is never validated; see GameDesign.platformer.
  const retainSinglePlatformerChapter =
    typeof v.platformer === 'object' &&
    v.platformer !== null &&
    !Array.isArray(v.platformer) &&
    (v.platformer as Record<string, unknown>).contractVersion === 2;
  const chapters = validateChapters(v.chapters, retainSinglePlatformerChapter);
  const onboarding = validateOnboarding(v.onboarding);
  const gameplay = v.gameplay === undefined || v.gameplay === null
    ? undefined
    : validateGameplayDesignV1(validateJsonObject(v.gameplay, 'gameplay'));
  let presentation: GameDesign['presentation'];
  if (v.presentation !== undefined && v.presentation !== null) {
    const p = validateJsonObject(v.presentation, 'presentation');
    for (const key of Object.keys(p)) if (!['contractVersion', 'hud', 'scene', 'effects'].includes(key)) fail(`presentation.${key} is not supported`);
    if (p.contractVersion !== 1) fail('presentation.contractVersion must equal 1');
    presentation = {
      contractVersion: 1,
      hud: validateMatchPresentationSpecV1(p.hud, 'presentation.hud'),
      scene: validateSceneCompositionSpecV1(p.scene, 'presentation.scene'),
      effects: validateEffectsBindingV1(p.effects, 'presentation.effects'),
    };
  }
  let audio: GameDesign['audio'];
  if (v.audio !== undefined && v.audio !== null) {
    const a = validateJsonObject(v.audio, 'audio');
    for (const key of Object.keys(a)) if (!['contractVersion', 'musicBrief', 'cueStyle'].includes(key)) fail(`audio.${key} is not supported`);
    if (a.contractVersion !== 1) fail('audio.contractVersion must equal 1');
    const musicBrief = optText(a.musicBrief, 'audio.musicBrief', 300);
    const cueStyle = optText(a.cueStyle, 'audio.cueStyle', 300);
    audio = { contractVersion: 1, ...(musicBrief === undefined ? {} : { musicBrief }), ...(cueStyle === undefined ? {} : { cueStyle }) };
  }
  const platformer = v.platformer === undefined || v.platformer === null ? undefined : v.platformer;

  return {
    summary,
    coreVerbs,
    mechanics,
    entities,
    controls,
    winCondition,
    ...(modeNotes !== undefined ? { modeNotes } : {}),
    ...(theme !== undefined ? { theme } : {}),
    ...(artDirection !== undefined ? { artDirection } : {}),
    ...(scenery !== undefined ? { scenery } : {}),
    ...(chapters !== undefined ? { chapters } : {}),
    ...(onboarding !== undefined ? { onboarding } : {}),
    ...(gameplay !== undefined ? { gameplay } : {}),
    ...(presentation !== undefined ? { presentation } : {}),
    ...(audio !== undefined ? { audio } : {}),
    ...(platformer !== undefined ? { platformer } : {}),
  };
}

/** Non-throwing form of {@link validateGameDesign}. */
export function isValidGameDesign(value: unknown): value is GameDesign {
  try {
    validateGameDesign(value);
    return true;
  } catch {
    return false;
  }
}

/** Caps for a watch report. Sized like the design caps they feed: an
 *  observations block is background material for ONE proposal turn, not a
 *  transcript — a model that writes an essay here is padding, and a padded
 *  block crowds out the creator's own concept in the prompt.
 *
 *  `hudMax` is 12, not 8: an arcade HUD really does carry that many readouts
 *  (Choplifter shows round, fuel, score, lives, and four rescue counters), and
 *  8 threw away otherwise-good watches. */
export const VIDEO_LIMITS = {
  summaryMax: 500,
  artStyleMax: 200,
  entitiesMax: 12,
  entityMaxLen: 60,
  featuresMax: 12,
  featureMaxLen: 200,
  hudMax: 12,
  hudMaxLen: 80,
  gameMaxLen: 80,
  titleMaxLen: 200,
  channelMaxLen: 120,
} as const;

function videoFail(msg: string): never {
  throw new PlatformError('validation', `invalid VideoObservations: ${msg}`);
}

/**
 * Validate + narrow a vision model's watch report. Throws `PlatformError`
 * ('validation') on anything STRUCTURALLY wrong — not an object, a required
 * field missing, an out-of-vocabulary worldStructure, a malformed color. The
 * caller's ONE retry re-asks with the message, and a second failure drops the
 * brief to `status:'failed'` rather than inventing observations.
 *
 * Over-LENGTH is handled differently: lists clamp to the cap and long entries
 * truncate, rather than throwing. These are OBSERVATIONS, not authored design
 * — nothing round-trips through them, so a model that lists one HUD element
 * too many has produced a formatting nit, not a wrong answer, and discarding
 * a whole watch (a browser session and a vision call) over it is the wrong
 * trade. Live Choplifter watches did exactly that before this split.
 */
export function validateVideoObservations(value: unknown): VideoObservations {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    videoFail('not an object');
  }
  const v = value as Record<string, unknown>;
  const L = VIDEO_LIMITS;

  const list = (raw: unknown, what: string, max: number, maxLen: number): string[] => {
    if (!Array.isArray(raw)) videoFail(`${what} must be an array`);
    return raw
      .slice(0, max)
      .map((x, i) => {
        if (typeof x !== 'string') videoFail(`${what}[${i}] must be a string`);
        return x.trim().slice(0, maxLen);
      })
      .filter((s) => s.length > 0);
  };

  /** Required, but clamped rather than rejected (see the doc comment). */
  const text = (raw: unknown, what: string, max: number): string => {
    if (typeof raw !== 'string') videoFail(`${what} must be a string`);
    const s = (raw as string).trim();
    if (!s) videoFail(`${what} is required`);
    return s.slice(0, max);
  };

  const summary = text(v.summary, 'summary', L.summaryMax);
  const artStyle = text(v.artStyle, 'artStyle', L.artStyleMax);
  const worldStructure = String(v.worldStructure) as VideoWorldStructure;
  if (!VIDEO_WORLD_STRUCTURES.includes(worldStructure)) {
    videoFail(`worldStructure must be one of ${VIDEO_WORLD_STRUCTURES.join('|')}`);
  }
  // The palette shares the design's validator (and its ≤8 cap) so a watched
  // palette and an authored one can never disagree about what a color is.
  const palette = validatePalette(v.palette, 'palette') ?? [];
  const entities = list(v.entities, 'entities', L.entitiesMax, L.entityMaxLen).map((s) =>
    s.toLowerCase(),
  );
  if (entities.length === 0) videoFail('entities must name at least one on-screen actor');
  const features = list(v.features, 'features', L.featuresMax, L.featureMaxLen);
  if (features.length === 0) videoFail('features must name at least one observed mechanic');
  const hud = list(v.hud ?? [], 'hud', L.hudMax, L.hudMaxLen);

  return { summary, artStyle, palette, worldStructure, entities, features, hud };
}
